import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const Dashboard = ({ data, user }) => {
  const { instance, accounts } = useMsal();

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState } = usePresence('projectDashboard', 'global', true);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter projects based on search term
  const filteredProjects = projects.filter(project => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    return (
      (project.job_number || '').toLowerCase().includes(search) ||
      (project.job_name || '').toLowerCase().includes(search)
    );
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setProjectsLoading(true);
      const projectsResponse = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/projects'
      );
      // Sort projects by job number (numeric if possible, otherwise alphabetic)
      const sortedProjects = [...(projectsResponse.data.projects || [])].sort((a, b) => {
        const numA = parseInt(a.job_number, 10);
        const numB = parseInt(b.job_number, 10);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return (a.job_number || '').localeCompare(b.job_number || '');
      });
      setProjects(sortedProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
    setProjectsLoading(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getDisciplineColor = (code) => {
    const colors = {
      'PIP': { bg: '#fee2e2', text: '#991b1b', border: '#fecaca' },
      'MEC': { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' },
      'ELE': { bg: '#fef3c7', text: '#92400e', border: '#fde68a' },
      'STR': { bg: '#d1fae5', text: '#065f46', border: '#a7f3d0' },
      'CIV': { bg: '#e0e7ff', text: '#3730a3', border: '#c7d2fe' },
      'HVA': { bg: '#ede9fe', text: '#5b21b6', border: '#ddd6fe' },
      'INS': { bg: '#fce7f3', text: '#9d174d', border: '#fbcfe8' },
    };
    return colors[code] || { bg: '#f3f4f6', text: '#374151', border: '#e5e7eb' };
  };

  // Show loading only while projects are being fetched
  if (projectsLoading && projects.length === 0) {
    return <div data-tutorial="projects-list" style={{ padding: '2rem', textAlign: 'center' }}>Loading projects...</div>;
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#f5f7fa' }}>
      {/* Header Section */}
      <div data-tutorial="projects-list" style={{ 
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        flexShrink: 0
      }}>
        <div data-tutorial="projects-list" style={{ 
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '1.25rem 2.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h1 data-tutorial="projects-list" style={{ 
              margin: 0, 
              fontSize: '2.25rem',
              color: '#111827',
              fontWeight: '700',
              letterSpacing: '-0.025em'
            }}>
              My Projects
            </h1>
            <p data-tutorial="projects-list" style={{ 
              margin: '0.5rem 0 0 0',
              color: '#6b7280',
              fontSize: '1rem'
            }}>
              {searchTerm ? `${filteredProjects.length} of ${projects.length}` : projects.length} {projects.length === 1 ? 'project' : 'projects'} {searchTerm ? 'found' : 'available'}
            </p>
          </div>
          
          {/* Presence indicator */}
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />

          {/* Search Input */}
          <div data-tutorial="search-bar" data-tutorial="projects-list" style={{ position: 'relative' }}>
            <svg 
              width="18" 
              height="18" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="#9ca3af" 
              strokeWidth="2"
              data-tutorial="projects-list" style={{
                position: 'absolute',
                left: '14px',
                top: '50%',
                transform: 'translateY(-50%)',
                pointerEvents: 'none'
              }}
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="M21 21l-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              placeholder="Search by name or job #..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-tutorial="projects-list" style={{
                padding: '0.75rem 1rem 0.75rem 2.75rem',
                fontSize: '0.95rem',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                width: '280px',
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                outline: 'none',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#dc2626';
                e.target.style.boxShadow = '0 0 0 3px rgba(220, 38, 38, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)';
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                data-tutorial="projects-list" style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: '#e5e7eb',
                  border: 'none',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0
                }}
                title="Clear search"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="3">
                  <path d="M18 6L6 18M6 6l12 12"></path>
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div data-tutorial="projects-list" style={{ 
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)',
        flex: 1,
        overflowY: 'auto'
      }}>
        <div data-tutorial="projects-list" style={{
          padding: '1.5rem 2.5rem 2.5rem 2.5rem',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          {projectsLoading ? (
            <div data-tutorial="projects-list" style={{
              padding: '4rem',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              textAlign: 'center'
            }}>
              <div data-tutorial="projects-list" style={{
                display: 'inline-flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1.25rem'
              }}>
                <div data-tutorial="projects-list" style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid #f3f4f6',
                  borderTop: '4px solid #dc2626',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }}></div>
                <div data-tutorial="projects-list" style={{ color: '#6b7280', fontSize: '1rem', fontWeight: '500' }}>
                  Loading projects...
                </div>
              </div>
            </div>
          ) : projects.length === 0 ? (
            <div data-tutorial="projects-list" style={{
              padding: '4rem',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              textAlign: 'center',
              border: '2px dashed #d1d5db'
            }}>
              <div data-tutorial="projects-list" style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>📋</div>
              <h3 data-tutorial="projects-list" style={{ color: '#111827', marginBottom: '0.5rem', fontWeight: '600' }}>No Projects Assigned</h3>
              <p data-tutorial="projects-list" style={{ color: '#6b7280', fontSize: '1rem', margin: 0 }}>
                Contact your administrator to request access to projects.
              </p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div data-tutorial="projects-list" style={{
              padding: '4rem',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              textAlign: 'center'
            }}>
              <div data-tutorial="projects-list" style={{ fontSize: '3.5rem', marginBottom: '1rem' }}>🔍</div>
              <h3 data-tutorial="projects-list" style={{ color: '#111827', marginBottom: '0.5rem', fontWeight: '600' }}>No Projects Found</h3>
              <p data-tutorial="projects-list" style={{ color: '#6b7280', fontSize: '1rem', margin: 0 }}>
                No projects match "{searchTerm}". Try a different search term.
              </p>
              <button
                onClick={() => setSearchTerm('')}
                data-tutorial="projects-list" style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div data-tutorial="projects-list" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.75rem'
            }}>
              {filteredProjects.map((project) => {
                const isHovered = hoveredRow === project.id;
                return (
                  <Link 
                    key={project.id} 
                    to={`/project/${project.id}`}
                    data-tutorial="project-card" style={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block'
                    }}
                    onMouseEnter={() => setHoveredRow(project.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        // The desktop layout needs ~806px for its fixed tracks plus
                        // padding, so it only applies from lg up - exactly as today.
                        // Narrower viewports stack instead, which keeps the Open button
                        // on screen (it used to be pushed outside the card).
                        gridTemplateColumns: { xs: '1fr', lg: '100px 1fr 160px 380px 110px' },
                        padding: { xs: '1rem 1.1rem', lg: '1.25rem 1.75rem' },
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        boxShadow: isHovered 
                          ? '0 8px 30px rgba(220, 38, 38, 0.15), 0 0 0 1px rgba(220, 38, 38, 0.1)' 
                          : '0 2px 8px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.02)',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        gap: { xs: '0.65rem', lg: '1.5rem' },
                        alignItems: { xs: 'flex-start', lg: 'center' },
                        transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
                        borderLeft: isHovered ? '4px solid #dc2626' : '4px solid transparent'
                      }}
                    >
                      {/* Job Number */}
                      <div>
                        <span style={{
                          display: 'inline-block',
                          background: isHovered 
                            ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' 
                            : '#f9fafb',
                          color: isHovered ? 'white' : '#6b7280',
                          padding: '0.5rem 0.875rem',
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                          fontWeight: '700',
                          letterSpacing: '0.025em',
                          fontFamily: "'SF Mono', 'Fira Code', monospace",
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          transition: 'all 0.2s ease'
                        }}>
                          {project.job_number}
                        </span>
                      </div>
                      
                      {/* Project Name */}
                      <div style={{ minWidth: 0 }}>
                        <Box sx={{ 
                          fontWeight: '600',
                          color: '#111827',
                          fontSize: '1rem',
                          marginBottom: '0.35rem',
                          lineHeight: '1.4',
                          // Truncating to one line is a desktop constraint from the narrow
                          // 1fr track; stacked cards have room to wrap instead.
                          whiteSpace: { xs: 'normal', lg: 'nowrap' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {project.job_name}
                        </Box>
                        <div style={{
                          fontSize: '0.8rem',
                          color: '#9ca3af',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          whiteSpace: 'nowrap'
                        }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                          {formatDate(project.created_at)}
                        </div>
                      </div>
                      
                      {/* Division */}
                      <div>
                        {project.division_name ? (
                          <div style={{ 
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '0.5rem',
                            padding: '0.4rem 0.75rem',
                            backgroundColor: '#f0fdf4',
                            borderRadius: '20px',
                            border: '1px solid #bbf7d0'
                          }}>
                            <span style={{
                              width: '6px',
                              height: '6px',
                              borderRadius: '50%',
                              backgroundColor: '#22c55e',
                              flexShrink: 0
                            }}></span>
                            <span style={{ 
                              fontSize: '0.8rem', 
                              color: '#15803d',
                              fontWeight: '500'
                            }}>
                              {project.division_name}
                            </span>
                          </div>
                        ) : (
                          <span style={{ 
                            color: '#d1d5db', 
                            fontSize: '0.85rem',
                            fontStyle: 'italic' 
                          }}>
                            No division
                          </span>
                        )}
                      </div>
                      
                      {/* Disciplines */}
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.4rem',
                        justifyContent: 'flex-start'
                      }}>
                        {project.disciplines && project.disciplines.length > 0 ? (
                          project.disciplines.map(disc => {
                            const colors = getDisciplineColor(disc.discipline_code);
                            return (
                              <span 
                                key={disc.id}
                                style={{
                                  padding: '0.3rem 0.6rem',
                                  backgroundColor: colors.bg,
                                  color: colors.text,
                                  borderRadius: '6px',
                                  fontSize: '0.7rem',
                                  fontWeight: '600',
                                  border: `1px solid ${colors.border}`,
                                  letterSpacing: '0.025em'
                                }}
                              >
                                {disc.discipline_code || disc.discipline_name}
                              </span>
                            );
                          })
                        ) : (
                          <span style={{ color: '#d1d5db', fontSize: '0.85rem' }}>—</span>
                        )}
                      </div>
                      
                      {/* Action Button */}
                      <Box sx={{ textAlign: { xs: 'left', lg: 'right' } }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.6rem 1.1rem',
                          background: isHovered 
                            ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' 
                            : '#f9fafb',
                          color: isHovered ? 'white' : '#6b7280',
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          transition: 'all 0.2s ease',
                          border: isHovered ? 'none' : '1px solid #e5e7eb',
                          boxShadow: isHovered ? '0 4px 12px rgba(220, 38, 38, 0.3)' : 'none'
                        }}>
                          Open
                          <svg 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2.5"
                            style={{
                              transform: isHovered ? 'translateX(3px)' : 'translateX(0)',
                              transition: 'transform 0.2s ease'
                            }}
                          >
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                          </svg>
                        </span>
                      </Box>
                    </Box>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default Dashboard;
